import React, { Suspense } from "react";

// Utils
import { TinaMarkdown } from 'tinacms/dist/rich-text';
import { graphql, useStaticQuery } from "gatsby";
import { useClass, addClassObjects } from "@breezerr";

// Components
import Logo from "@brz-components/elements/logo";
import { TextLink } from "@brz-components/elements/text.link";
import { usePage } from "@brz-utils/contexts/page.context";
import { HeaderNavigationDataModel } from "@brz-types/navigation.type";
import { Button, ButtonBase, ClickAwayListener, Fade, IconButton, MenuItem } from "@mui/material";
import { MegaMenu } from "./mega.menu";
import { CrossIcon } from "@brz-components/icons/icon.cross";
import { DropDownButton } from "@brz-components/elements/dropdown.button";
import { SimpleMenu } from "./simple.menu";
import { useBreakpoint } from "@brz-utils/contexts/is.breakpoint";



const MobileNavigation = React.lazy(() => import(/* webpackMode: "lazy" */ './mobile.nav'));


export const INITIAL_HEADER_HEIGHT = 100;

// addClassObjects({
//     breakpoint: '',
//     rule: {
//         body: {
//             marginTop: `${INITIAL_HEIGHT / 10}rem !important`,
//         }
//     }
// });

type Props = {
    children?: React.ReactNode | React.ReactNode[];
};


export const Header = (props: Props) => {
    const { data } = useStaticQuery<HeaderNavigationDataModel>(navQuery);
    const { isMobile, isTablet, breakpoint } = useBreakpoint();

    // console.log(isMobile, isTablet, breakpoint);
    
    
    // Contexts
    const { page } = usePage();

    // States
    const [scrolled, updateScrolled] = React.useState(0);
    const [open, setOpen] = React.useState<string | null>(null);
    
    const anchorElRefs = data.left.map(() => React.useRef<HTMLButtonElement>(null));

    // On Mount
    React.useEffect(() => {
        if (typeof window !== 'undefined') {
            window.addEventListener('scroll', handleWindowScroll, { passive: true })
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('scroll', handleWindowScroll);
            }
        }
    }, []);
    


    const handleWindowScroll = () => {
        // Calculate scrolled percent
        let pos = window.scrollY;
        let cap = 100;
        let perc = pos * 100 / cap;

        if (perc >= cap) {
            perc = cap;
        }

        if (perc < cap) {
            updateScrolled(perc);
        } else if (perc >= cap && scrolled < cap) {
            updateScrolled(cap);
        }
    };

    const allDisabled = React.useMemo(() => {
        const options = [page.navigation?.disableButtons, page.navigation?.disableLeft, page.navigation?.disableLogo, page.navigation?.disableRight];
        if ( options.includes(false) || options.includes(undefined) ) {
            return false;
        }

        return true;
    }, [page.navigation?.disableButtons, page.navigation?.disableLeft, page.navigation?.disableLogo, page.navigation?.disableRight])


    return !allDisabled ? <>
        <header 
            className={useClass(`p-fixed top-0x left-0x w-100vw z-index-100`)}
        >
            <div 
                className={useClass('p-absolute z-index-0 bg-color-white w-100% h-100% left-0x top-0x')}
                style={{
                    // transition: 'opacity 250ms ease',
                    opacity: open && open.indexOf('megaMenu') > -1 ? 1 : (Math.min(scrolled * 2, 100) / 100).toFixed(2)
                }}
            ></div>

            {page?.notification ? (
                <div className={useClass(`p-relative z-index-10 h-4.5rem bg-color-${page.notification.color}`)}>
                    <div className={useClass('container d-flex align-items-center justify-content-center text-center h-100% my-0x__p')}>
                        <TinaMarkdown content={page.notification.body} />
                    </div>
                </div>
            ) : null}

            <div 
                className={useClass(`p-relative z-index-10 container d-flex align-items-center justify-content-between min-h-${INITIAL_HEADER_HEIGHT - 25}px md:min-h-${INITIAL_HEADER_HEIGHT}px max-h-${INITIAL_HEADER_HEIGHT - 25}px md:max-h-${INITIAL_HEADER_HEIGHT}px`)}
                style={{
                    height: !!scrolled && !isMobile ? INITIAL_HEADER_HEIGHT - (25 * (scrolled/100)) : undefined
                }}
            >
                {/* Logo + Left-side Navigation */}
                <div className={useClass('p-relative d-flex justify-content-start align-items-center min-h-4.5rem')}>

                    {/* Burger Icon */}
                    <ButtonBase
                        className={useClass('d-flex md:d-none! w-4.5rem h-4.5rem radius-100%')}
                        onClick={() => {
                            setTimeout(() => setOpen('mobile'), 200)
                        }}
                        aria-label="Navigation Button"
                        sx={{
                            position: 'absolute',
                            left: '-1.2rem',
                            top: '0rem'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12">
                            <g transform="translate(1 1)">
                                <line x2="14" fill="none" stroke="#354a5e" strokeLinecap="round" strokeWidth="2" />
                                <line x2="14" transform="translate(0 5)" fill="none" stroke="#354a5e" strokeLinecap="round" strokeWidth="2" />
                                <line x2="5" transform="translate(0 10)" fill="none" stroke="#354a5e" strokeLinecap="round" strokeWidth="2" />
                            </g>
                        </svg>
                    </ButtonBase>


                    {/* Logo */}
                    {page.navigation?.disableLogo ? null : (
                        <TextLink 
                            href="/" 
                            className={useClass('d-block z-index-110 ml-4rem md:-ml-0.2rem md:-mt-0.5rem no-underline!')}
                        >
                            <Logo />
                        </TextLink>
                    )}

                    {/* Left side buttons */}
                    {data.left && page.navigation?.disableLeft !== true ? (
                        <>
                            <div className={useClass('d-none lg:d-flex px-0.6rem py-0.5rem bg-color-grey:300 radius-2.1rem ml-4rem z-index-110')}>{
                                data.left.map((item, index) => {
                                    return <ButtonBase
                                        ref={anchorElRefs[index]}
                                        component={'button'}
                                        key={index}
                                        className={useClass(`transition-background-300ms-ease bg-color-${open === `left:${item._template}:${index}` ? 'white' : 'grey:300'} text-size-1.3rem text-500`)}
                                        onClick={() => {
                                            setTimeout(() => {
                                                setOpen(open === `left:${item._template}:${index}` ? null : `left:${item._template}:${index}`);
                                            }, 10)
                                        }}
                                        sx={{
                                            paddingY: '0.2rem',
                                            paddingX: '1.5rem',
                                            borderRadius: '1.6rem'
                                        }}
                                    >{item.title}</ButtonBase>
                                })
                            }</div>

                            <ButtonBase
                                className={useClass('d-none md:d-flex lg:d-none text-size-1.3rem text-500 bg-color-grey:300 px-1.5rem py-0.55rem radius-1.8rem ml-4rem')}
                                component={'button'}
                                onClick={() => setTimeout(() => setOpen('mobile'), 200)}
                            >{'Products'}</ButtonBase>
                        </>
                    ) : null}
                </div>

                {/* Right Side Navigation (links, buttons, etc...) */}
                <div className={useClass('p-relative d-none sm:d-block')}>
                    <Fade in={!open || open.indexOf('megaMenu') === -1} appear>
                        <nav className={useClass('d-flex justify-content-end align-items-center')}>{
                            data.right.map((item, index) => {
                                switch (item._template) {
                                    case page.navigation?.disableRight ? null : 'linkItem':
                                        return <Button
                                            key={index}
                                            className={useClass(`ml-0.5rem d-none md:d-flex`)}
                                            href={item.link}
                                            size="small"
                                            variant="text"
                                            color="primary"
                                        >{item.label}</Button>

                                    case page.navigation?.disableButtons ? null : 'buttonItem':
                                        return <Button
                                            key={index}
                                            className={useClass(`ml-2rem ${item.link.indexOf('get-started') > -1 ? '' : 'd-none xs:d-flex'}`)}
                                            href={page.navigation?.rightSideButtonOverride?.link || item.link}
                                            size="medium"
                                            variant="outlined"
                                            color="primary"
                                        >{page.navigation?.rightSideButtonOverride?.label || item.label}</Button>;

                                    case 'dropdownItem':
                                        return page.navigation?.disableRight ? null : <DropDownButton
                                            key={index}
                                            label={item.label}
                                            DropDownProps={{
                                                align: 'right',
                                                className: useClass('min-w-15rem')
                                            }}
                                            ButtonProps={{
                                                size: 'small',
                                                variant: 'text',
                                                color: 'primary',
                                                className: useClass('ml-0.5rem d-none md:d-flex')
                                            }}
                                        >{item.items.map((menuItem, index) => {
                                            return <MenuItem key={index} href={menuItem.link} component={TextLink}>{menuItem.label}</MenuItem>
                                        })}</DropDownButton>;
                                
                                    default:
                                        return null;
                                }
                            })
                        }</nav>
                    </Fade>
                    <Fade in={open && open.indexOf('megaMenu') > -1 ? true : false}>
                        <IconButton className={useClass(`p-absolute top-0.7rem right-0rem bg-color-grey:50 w-3.5rem h-3.5rem`)} onClick={() => setOpen(null)}>
                            <CrossIcon />
                        </IconButton>
                    </Fade>
                </div>
            </div>

            <ClickAwayListener onClickAway={() => {
                if (!isMobile) {
                    setOpen(null);                    
                }
            }}>
                <div>
                    {data.left.map((item, index) => {
                        if (item._template === 'megaMenu') {
                            return <MegaMenu key={index} show={open === `left:${item._template}:${index}`} data={item} onClose={() => setOpen(null)} />
                        }

                        if (item._template === 'simpleMenu') {
                            return <SimpleMenu key={index} anchorEl={anchorElRefs[index].current} show={open === `left:${item._template}:${index}`} data={item} onClose={() => setOpen(null)} />
                        }

                        return null;
                    })}
                </div>
            </ClickAwayListener>
        </header>
        
        {/* Mobile Navigation */}
        <Suspense>
            {isMobile || isTablet ? (
                <MobileNavigation
                    show={open === 'mobile'}
                    onClose={() => setOpen(null)}
                    data={data}
                />
            ) : null}
        </Suspense>
    </> : (!!props.children ? <>
        <header className={useClass(`p-fixed top-0x left-0x w-100vw z-index-100`)}>
            <div 
                className={useClass('p-absolute z-index-0 bg-color-white w-100% h-100% left-0x top-0x')}
                style={{
                    opacity: open && open.indexOf('megaMenu') > -1 ? 1 : (Math.min(scrolled * 2, 100) / 100).toFixed(2)
                }}
            ></div>

            <div 
                className={useClass(`p-relative z-index-10 container d-flex align-items-center justify-content-between min-h-${INITIAL_HEADER_HEIGHT - 25}px md:min-h-${INITIAL_HEADER_HEIGHT}px max-h-${INITIAL_HEADER_HEIGHT - 25}px md:max-h-${INITIAL_HEADER_HEIGHT}px`)}
                style={{
                    height: !!scrolled && !isMobile ? INITIAL_HEADER_HEIGHT - (25 * (scrolled/100)) : undefined
                }}
            >
                {props.children}
            </div>
        </header>
    </> : null);
};


const navQuery = graphql`
    query {
        data: navigationJson {
            left {
                title
                heading
                items {
                    title
                    description
                    icon
                    type
                    link
                    
                    image {
                        src
                        width
                        height
                        aspectRatio
                        alt
                    }
                    buttons {
                        label
                        link
                    }
                    columns {
                        title
                        links {
                            label
                            link
                        }
                    }
                }
                _template
            } # end left

            right {
                label
                link
                items {
                    label
                    link
                }
                _template
            } # end right


        } # end navigationJson
    }
`;